import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getClient } from "@vixio/data";
import { Button, Callout } from "@vixio/ui";
import { INFRA_PANELS } from "@vixio/core-ui";
import { AuthLayout } from "../auth/AuthLayout";
import { useAuth } from "../auth/AuthProvider";
import { TotpChallengeForm } from "../auth/mfa";
import { closeGateSession, GATE_MESSAGES, isHttpsUrl, openGateSession, type GateError } from "./gate";


type State = { kind: "working" } | { kind: "done"; redirect: string | null } | { kind: "error"; error: GateError };

/**
 * /portao?next=<url>: o Traefik manda para cá quem abre um painel de infraestrutura sem o cookie do portão.
 * Sem sessão, RequireAuth leva ao login (e volta para cá, passando pelo 2FA do SessionGate quando exigido); com sessão,
 * o worker confere o papel e grava o cookie, e a pessoa segue para o painel.
 */
export function GatePage() {
  const [params] = useSearchParams();
  const { session, refreshAal } = useAuth();
  const raw = params.get("next");
  const next = isHttpsUrl(raw) ? raw : null;
  const [state, setState] = useState<State>({ kind: "working" });
  const [factorId, setFactorId] = useState<string | null>(null);
  const started = useRef(false);

  const run = useCallback(async () => {
    setState({ kind: "working" });
    if (raw && !next) {
      setState({ kind: "error", error: "invalid_next" });
      return;
    }
    const { data } = await getClient().auth.getSession();
    const token = data.session?.access_token ?? session?.access_token;
    if (!token) {
      setState({ kind: "error", error: "invalid_token" });
      return;
    }
    const r = await openGateSession(token, next);
    if (!r.ok) {
      if (r.error === "mfa_required") {
        const { data: f } = await getClient().auth.mfa.listFactors();
        setFactorId(f?.totp?.find((x) => x.status === "verified")?.id ?? null);
      }
      setState({ kind: "error", error: r.error });
      return;
    }
    setState({ kind: "done", redirect: r.redirect });
    if (r.redirect) window.location.replace(r.redirect);
  }, [raw, next, session]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run();
  }, [run]);

  if (state.kind === "working") {
    return (
      <AuthLayout title="Painéis de infraestrutura" subtitle="Liberando o acesso">
        <p className="muted" aria-busy="true">Conferindo a sua permissão</p>
      </AuthLayout>
    );
  }
  if (state.kind === "done") {
    return (
      <AuthLayout title="Acesso liberado" subtitle={state.redirect ? "Abrindo o painel" : "Escolha o painel"}>
        <div className="vx-stack">
          {state.redirect ? (
            <p>
              Se a página não abrir sozinha, <a href={state.redirect}>continue para o painel</a>.
            </p>
          ) : (
            <ul className="vx-stack" style={{ paddingLeft: 18 }}>
              {INFRA_PANELS.map((p) => (
                <li key={p.href}>
                  <a href={p.href}>{p.title} ({p.tool})</a>
                </li>
              ))}
            </ul>
          )}
          <p className="text-2">O acesso vale por 12 horas neste navegador.</p>
        </div>
      </AuthLayout>
    );
  }
  const msg = GATE_MESSAGES[state.error];
  return (
    <AuthLayout title={msg.title}>
      <div className="vx-stack">
        <Callout tone={state.error === "mfa_required" ? "info" : "crit"}>{msg.text}</Callout>
        {state.error === "mfa_required" && factorId ? (
          <TotpChallengeForm
            factorId={factorId}
            onDone={async () => {
              await refreshAal();
              await run();
            }}
          />
        ) : null}
        <div className="vx-row" style={{ gap: 12, flexWrap: "wrap" }}>
          {["unavailable", "too_many_requests", "gate_not_configured"].includes(state.error) ? (
            <Button onClick={() => void run()}>Tentar de novo</Button>
          ) : null}
          {state.error === "invalid_token" ? (
            <Button onClick={() => void getClient().auth.signOut()}>Sair e entrar de novo</Button>
          ) : null}
          <Link to="/">Voltar ao painel</Link>
        </div>
      </div>
    </AuthLayout>
  );
}

/** /portao/sair: encerra o acesso aos painéis de infraestrutura neste navegador (apaga o cookie do portão). */
export function GateLogoutPage() {
  const [done, setDone] = useState<boolean | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void closeGateSession().then(setDone);
  }, []);
  return (
    <AuthLayout title="Painéis de infraestrutura">
      <div className="vx-stack">
        {done === null ? <p className="muted">Encerrando o acesso</p> : null}
        {done === true ? <Callout tone="ok">Acesso aos painéis encerrado neste navegador.</Callout> : null}
        {done === false ? <Callout tone="crit">{GATE_MESSAGES.unavailable.text}</Callout> : null}
        <Link to="/">Voltar ao painel</Link>
      </div>
    </AuthLayout>
  );
}
